import { authErrorMessage } from "./authMessages";
import { authRepository } from "./authRepository";

/** Each call resolves to an error sentence to show, or null when it worked. */
export const authService = {
  async logIn(email: string, password: string) {
    const { error } = await authRepository.signIn(email, password);
    return error ? authErrorMessage(error) : null;
  },
  /** `confirmEmail` is true when the account waits on its confirmation link. */
  async signUp(email: string, password: string) {
    const { data, error } = await authRepository.signUp(email, password);
    return error
      ? { error: authErrorMessage(error), confirmEmail: false }
      : { error: null, confirmEmail: !data.session };
  },
  /**
   * Sends the browser to the provider's consent screen, then back to this
   * app signed in. The origin must be in the Supabase project's allowed
   * redirect URLs. Resolves to an error sentence only if that fails.
   */
  async signInWithProvider(provider: "google" | "facebook") {
    const { error } = await authRepository.signInWithProvider(
      provider,
      window.location.origin,
    );
    return error ? authErrorMessage(error) : null;
  },
  /**
   * Emails a link back to this app, which opens the new-password screen.
   * The origin must be in the Supabase project's allowed redirect URLs.
   */
  async sendPasswordReset(email: string) {
    const { error } = await authRepository.sendPasswordReset(
      email,
      window.location.origin,
    );
    return error ? authErrorMessage(error) : null;
  },
  async updatePassword(password: string) {
    const { error } = await authRepository.updatePassword(password);
    return error ? authErrorMessage(error) : null;
  },
  /**
   * Deletes the account and everything in it. On success the session is
   * already invalid, so it is cleared locally and the app returns to the
   * landing page.
   */
  async deleteAccount() {
    const message = await authRepository.deleteAccount();
    if (message) return message;
    await authRepository.signOut();
    return null;
  },
  async signOut() {
    const { error } = await authRepository.signOut();
    return error ? authErrorMessage(error) : null;
  },
};
